/**
 * AvailableOffers - Employee Dashboard for Broadcast Offers
 *
 * Shows all active broadcasts in the employee's company.
 * Employees can view masked lead data, see offers, and claim them.
 *
 * Layout: Progressive disclosure — collapsed summary cards expand to reveal
 * structured lead info + rich offer cards matching OffersComparison.js format.
 *
 * Route: /leads/available-offers
 * Who sees it: company_user, company_owner, company_admin
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Badge } from 'primereact/badge';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Paginator } from 'primereact/paginator';
import { Dropdown } from 'primereact/dropdown';
import { Divider } from 'primereact/divider';
import { Tooltip } from 'primereact/tooltip';
import { Card } from 'primereact/card';
import { Skeleton } from 'primereact/skeleton';
import { leadBroadcastService, getClaimStatusConfig, formatCurrency, formatLoanAmount } from '../../services/leadBroadcastService';
import { getProcessingStatusConfig } from '../../services/leadProcessingService';
import { getProductLoanTypes } from '../../services/offerCalculationService';
import OfferStatusBadge from '../../components/Leads/OfferStatusBadge';
import RegenerateOffersDialog from '../../components/Leads/company/RegenerateOffersDialog';
import OffersComparison from '../../components/Offers/OffersComparison';

const ITEMS_PER_PAGE = 15;

const FILTER_OPTIONS = [
    { label: 'All Broadcasts', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Processor Assigned', value: 'assigned' },
    { label: 'With My Claims', value: 'claimed' },
    { label: 'Not Claimed', value: 'unclaimed' },
];

/** Display product type — comes as loan type name or fallback slug */
const getProductLabel = (type) => {
    if (!type) return '—';
    // If already readable (contains space), return as-is
    if (type.includes(' ')) return type;
    // Convert slug: personal_loan → Personal Loan
    return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

/**
 * Bucket a broadcast by its created_at date into "Today" / "Yesterday" /
 * a weekday-and-date label (this week) / a full date label (older).
 *
 * Returns:
 *   - `key`   — stable identifier used to detect bucket changes during render
 *               (one header per consecutive run of broadcasts with the same key)
 *   - `label` — human-readable section header text shown to the user
 *   - `date`  — the broadcast's local Date for sorting / debugging
 *
 * "Today" and "Yesterday" compare on LOCAL calendar day, not 24-hour delta,
 * so a broadcast created at 11:55 PM yesterday still groups under "Yesterday"
 * even if the user opens the page at 12:05 AM today (a 10-minute gap).
 */
const getBroadcastDateBucket = (broadcast) => {
    const raw = broadcast?.created_at;
    if (!raw) return { key: 'undated', label: 'Undated', date: null };
    const d = new Date(raw);
    if (isNaN(d.getTime())) return { key: 'undated', label: 'Undated', date: null };

    const startOfDay = (dt) => new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    const broadcastDay = startOfDay(d);
    const today = startOfDay(new Date());
    const diffDays = Math.floor((today.getTime() - broadcastDay.getTime()) / (24 * 60 * 60 * 1000));

    const key = `${broadcastDay.getFullYear()}-${broadcastDay.getMonth() + 1}-${broadcastDay.getDate()}`;
    const weekdayLong = d.toLocaleDateString('en-IN', { weekday: 'long' });
    const dateShort = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    const dateFull = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

    if (diffDays === 0) return { key, label: `Today — ${weekdayLong}, ${dateFull}`, date: d };
    if (diffDays === 1) return { key, label: `Yesterday — ${weekdayLong}, ${dateFull}`, date: d };
    if (diffDays < 7) return { key, label: `${weekdayLong}, ${dateFull}`, date: d };
    return { key, label: dateFull, date: d };
};

/** Get staleness info for a broadcast based on last offer generation date */
const getOffersAge = (broadcast) => {
    const history = broadcast.regeneration_history;
    const lastDate = history?.length
        ? history[history.length - 1]?.generated_at
        : broadcast.created_at;

    if (!lastDate) return null;

    const days = Math.floor((Date.now() - new Date(lastDate).getTime()) / (1000 * 60 * 60 * 24));

    if (days <= 7) return { days, severity: 'success', label: 'Fresh', icon: 'pi-check-circle' };
    if (days <= 30) return { days, severity: 'warning', label: `${days}d old`, icon: 'pi-clock' };
    return { days, severity: 'danger', label: `${days}d old`, icon: 'pi-exclamation-triangle' };
};

const AvailableOffers = () => {
    const toast = useRef(null);
    const navigate = useNavigate();

    // Data states
    const [broadcasts, setBroadcasts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [leadDataCache, setLeadDataCache] = useState({});
    const [broadcastDetailsCache, setBroadcastDetailsCache] = useState({}); // offers, claims, eligibility loaded on expand
    const [leadDataMasked, setLeadDataMasked] = useState({}); // tracks masked flag per broadcast
    const [leadDataLoading, setLeadDataLoading] = useState({});
    const [expandedBroadcasts, setExpandedBroadcasts] = useState({});

    // Offer navigation per broadcast (sliding window)
    const [offerStartIndex, setOfferStartIndex] = useState({}); // { broadcastId: startIndex }
    const [isMobile, setIsMobile] = useState(false);
    const MAX_VISIBLE_OFFERS = 3;

    // Detect mobile screen
    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 576);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Regenerate offers dialog
    const [regenVisible, setRegenVisible] = useState(false);
    const [regenBroadcast, setRegenBroadcast] = useState(null);

    // Per-broadcast loading state for the View Payslip button so users get a
    // spinner during the on-demand signed-URL fetch + can't double-click.
    const [payslipLoading, setPayslipLoading] = useState({});

    // Inline payslip viewer dialog. Replaces the old "open N browser tabs"
    // pattern that lost 2 of 3 tabs to popup blockers — see the bug captured
    // in the earlier conversation. The dialog shows one payslip at a time
    // with Previous/Next nav, lets the user export the current page to a
    // browser tab on demand.
    const [payslipViewerVisible, setPayslipViewerVisible] = useState(false);
    const [payslipUrls, setPayslipUrls] = useState([]);
    const [payslipIndex, setPayslipIndex] = useState(0);

    // Pagination
    const [first, setFirst] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);

    // Filter, search & dates
    const [filter, setFilter] = useState('all');
    const [loanTypeFilter, setLoanTypeFilter] = useState(null);
    const [loanTypes, setLoanTypes] = useState([]);
    const [searchInput, setSearchInput] = useState(''); // immediate (typed)
    const [search, setSearch] = useState('');           // committed (sent to API)
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const aoMounted = useRef(false);

    // Load loan types for product filter dropdown
    useEffect(() => {
        getProductLoanTypes().then(res => {
            if (res.success) setLoanTypes(res.data || []);
        }).catch(() => {});
    }, []);

    /** Format Date → YYYY-MM-DD for API */
    const formatDateParam = (date) => {
        if (!date) return null;
        const d = new Date(date);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    // Claim dialog
    const [claimDialogVisible, setClaimDialogVisible] = useState(false);
    const [claimingOffer, setClaimingOffer] = useState(null);
    const [claimMessage, setClaimMessage] = useState('');
    const [submittingClaim, setSubmittingClaim] = useState(false);

    // Withdraw dialog
    const [withdrawDialogVisible, setWithdrawDialogVisible] = useState(false);
    const [withdrawingClaim, setWithdrawingClaim] = useState(null);
    const [withdrawing, setWithdrawing] = useState(false);

    // Make Processor — inline confirm (for lead owners on own broadcasts)
    const [processorConfirmTarget, setProcessorConfirmTarget] = useState(null); // { claim, broadcast }
    const [changingProcessor, setChangingProcessor] = useState(false);

    // Approve/Reject dialogs — for lead owners on own broadcasts
    const [approveDialogVisible, setApproveDialogVisible] = useState(false);
    const [approvingClaim, setApprovingClaim] = useState(null);
    const [approving, setApproving] = useState(false);
    const [rejectDialogVisible, setRejectDialogVisible] = useState(false);
    const [rejectingClaim, setRejectingClaim] = useState(null);
    const [rejectReason, setRejectReason] = useState('');
    const [rejecting, setRejecting] = useState(false);

    // ══════════════════════════════════════════
    // DATA ACCESS HELPERS
    // ══════════════════════════════════════════

    // Merge broadcast (listing) with cached expand details for full data access
    const getEnriched = (b) => {
        const details = broadcastDetailsCache[b.id];
        if (!details) return b;
        return { ...b, ...details };
    };

    // Listing-level helpers (work with slim listing data)
    const getMyClaimsCount = (b) => b.claims_summary?.my_claims_count ?? b.claims_summary?.my_claims?.length ?? 0;
    const getTotalClaims = (b) => b.claims_summary?.total_claims || 0;

    // Expand-level helpers (use enriched broadcast with full details)
    const getMyClaimOnOffer = (b, offerId) => getEnriched(b).claims_summary?.my_claims?.find(c => c.offer_id === offerId);
    const getOfferClaimCount = (b, offerId) => getEnriched(b).claims_summary?.per_offer?.[offerId]?.total || 0;
    const getOffers = (b) => {
        const enriched = getEnriched(b);
        const raw = enriched.offers || enriched.broadcast_offers?.offers || [];
        return raw.map((o, idx) => ({
            ...o,
            offer_id: o.offer_id || o.id || `offer_${idx + 1}`,
        }));
    };

    // Processor state helpers
    const isProcessorSelected = (b) => b.status === 'processor_selected';
    const getProcessorName = (b) => b.selected_processor || getEnriched(b).claims_summary?.processor_info?.processor_name;
    const getSelectedOfferId = (b) => b.selected_offer_id || getEnriched(b).claims_summary?.processor_info?.selected_offer_id;
    const getSelectedAt = (b) => b.selected_at || getEnriched(b).claims_summary?.processor_info?.selected_at;
    const isSelectedOffer = (b, offerId) => {
        const selectedId = getSelectedOfferId(b);
        if (!selectedId || !offerId) return false;
        return String(selectedId) === String(offerId);
    };

    // Own broadcast helper
    const isOwnBroadcast = (b) => b.is_own_broadcast === true || getEnriched(b).is_own_broadcast === true;

    // Sort offers: selected offer first → offers with claims → offers without claims
    const getSortedOffers = (b) => {
        const offers = getOffers(b);
        const score = (o) => {
            if (isSelectedOffer(b, o.offer_id)) return 3;
            if (getOfferClaimCount(b, o.offer_id) > 0) return 2;
            return 1;
        };
        return [...offers].sort((x, y) => score(y) - score(x));
    };

    // ══════════════════════════════════════════
    // DATA LOADING
    // ══════════════════════════════════════════

    const loadBroadcasts = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const result = await leadBroadcastService.getAvailableBroadcasts({
                page,
                limit: ITEMS_PER_PAGE,
                status: filter,
                search: search,
                date_from: formatDateParam(dateFrom),
                date_to: formatDateParam(dateTo),
                loan_type_id: loanTypeFilter || undefined,
            });

            if (result.success) {
                setBroadcasts(result.data?.broadcasts || []);
                setTotalRecords(result.data?.total || 0);
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: result.message || 'Failed to load broadcasts',
                    life: 3000,
                });
            }
        } catch (error) {
            console.error('Failed to load broadcasts:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load available offers. Please try again.',
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    }, [filter, search, dateFrom, dateTo, loanTypeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    // Mount guard + debounce: first call fires immediately, subsequent filter/search/date changes debounce
    useEffect(() => {
        if (!aoMounted.current) {
            aoMounted.current = true;
            loadBroadcasts(1);
            return;
        }
        setFirst(0);
        setExpandedBroadcasts({});
        const timer = setTimeout(() => loadBroadcasts(1), 300);
        return () => clearTimeout(timer);
    }, [filter, search, dateFrom, dateTo, loanTypeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const onPageChange = (e) => {
        setFirst(e.first);
        const page = Math.floor(e.first / ITEMS_PER_PAGE) + 1;
        loadBroadcasts(page);
    };

    const handleSearchSubmit = () => {
        setSearch(searchInput.trim());
        setFirst(0);
        setExpandedBroadcasts({});
    };

    // Refresh: clear all caches, reload listing, then re-fetch data for any expanded broadcasts
    const handleRefresh = useCallback(async () => {
        const currentlyExpanded = { ...expandedBroadcasts };
        // Clear caches so expand re-fetches fresh data
        setLeadDataCache({});
        setLeadDataMasked({});
        setBroadcastDetailsCache({});
        // Reload listing
        const page = Math.floor(first / ITEMS_PER_PAGE) + 1;
        await loadBroadcasts(page);
        // Re-fetch expand data for broadcasts that were open
        const expandedIds = Object.keys(currentlyExpanded).filter((id) => currentlyExpanded[id]);
        expandedIds.forEach((id) => {
            // loadLeadData won't skip because cache was cleared
            loadLeadData(parseInt(id));
        });
    }, [expandedBroadcasts, first, loadBroadcasts]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleClearFilters = () => {
        setFilter('all');
        setSearchInput('');
        setSearch('');
        setDateFrom(null);
        setDateTo(null);
        setFirst(0);
        setExpandedBroadcasts({});
    };

    // ── Lead Data Loading (per broadcast, with loading state) ──

    const loadLeadData = async (broadcastId) => {
        if (leadDataCache[broadcastId]) return;

        setLeadDataLoading((prev) => ({ ...prev, [broadcastId]: true }));
        try {
            const result = await leadBroadcastService.getBroadcastLeadData(broadcastId);
            if (result.success) {
                setLeadDataCache((prev) => ({ ...prev, [broadcastId]: result.data }));
                setLeadDataMasked((prev) => ({ ...prev, [broadcastId]: result.masked !== false }));
                // Store broadcast details (offers, claims, eligibility) from expand API
                if (result.broadcast_details) {
                    setBroadcastDetailsCache((prev) => ({ ...prev, [broadcastId]: result.broadcast_details }));
                }
            }
        } catch (error) {
            console.error('Failed to load lead data:', error);
        } finally {
            setLeadDataLoading((prev) => ({ ...prev, [broadcastId]: false }));
        }
    };

    const toggleExpand = (broadcastId) => {
        const isExpanding = !expandedBroadcasts[broadcastId];
        setExpandedBroadcasts((prev) => ({
            ...prev,
            [broadcastId]: isExpanding,
        }));

        if (isExpanding) {
            loadLeadData(broadcastId);
        }
    };

    // ══════════════════════════════════════════
    // CLAIM ACTIONS
    // ══════════════════════════════════════════

    const openClaimDialog = (broadcast, offer) => {
        setClaimingOffer({ broadcast, offer });
        setClaimMessage('');
        setClaimDialogVisible(true);
    };

    const submitClaim = async () => {
        if (!claimingOffer) return;

        setSubmittingClaim(true);
        try {
            const result = await leadBroadcastService.claimOffer(
                claimingOffer.broadcast.id,
                claimingOffer.offer.offer_id,
                claimMessage || null
            );

            if (result.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Claimed',
                    detail: 'Offer claimed successfully. Waiting for approval.',
                    life: 3000,
                });
                setClaimDialogVisible(false);
                const page = Math.floor(first / ITEMS_PER_PAGE) + 1;
                loadBroadcasts(page);
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: result.message || 'Failed to claim offer',
                    life: 4000,
                });
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to claim offer. Please try again.',
                life: 4000,
            });
        } finally {
            setSubmittingClaim(false);
        }
    };

    const handleChangeProcessor = async () => {
        if (!processorConfirmTarget) return;
        const { claim, broadcast } = processorConfirmTarget;
        setChangingProcessor(true);
        try {
            const result = await leadBroadcastService.assignProcessor(
                broadcast.id,
                claim.claimed_by_user_id,
                claim.offer_id
            );
            if (result.success) {
                const name = claim.claimed_by?.name || 'Employee';
                toast.current?.show({ severity: 'success', summary: 'Processor Changed', detail: `${name} is now the processor.`, life: 4000 });
                setProcessorConfirmTarget(null);
                loadBroadcasts(Math.floor(first / ITEMS_PER_PAGE) + 1);
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: result.message || 'Failed to change processor', life: 3000 });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to change processor. Please try again.', life: 3000 });
        } finally {
            setChangingProcessor(false);
        }
    };

    const openApproveDialog = (claim) => {
        setApprovingClaim(claim);
        setApproveDialogVisible(true);
    };

    const handleApprove = async () => {
        if (!approvingClaim) return;
        setApproving(true);
        try {
            const result = await leadBroadcastService.approveClaim(approvingClaim.id);
            if (result.success) {
                toast.current?.show({ severity: 'success', summary: 'Approved', detail: `Claim by ${approvingClaim.claimed_by?.name || 'employee'} approved.`, life: 4000 });
                setApproveDialogVisible(false);
                setApprovingClaim(null);
                loadBroadcasts(Math.floor(first / ITEMS_PER_PAGE) + 1);
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: result.message || 'Failed to approve claim', life: 3000 });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to approve claim.', life: 3000 });
        } finally {
            setApproving(false);
        }
    };

    const openRejectDialog = (claim) => {
        setRejectingClaim(claim);
        setRejectReason('');
        setRejectDialogVisible(true);
    };

    const handleReject = async () => {
        if (!rejectingClaim) return;
        setRejecting(true);
        try {
            const result = await leadBroadcastService.rejectClaim(rejectingClaim.id, rejectReason || null);
            if (result.success) {
                toast.current?.show({ severity: 'info', summary: 'Rejected', detail: `Claim by ${rejectingClaim.claimed_by?.name || 'employee'} rejected.`, life: 3000 });
                setRejectDialogVisible(false);
                setRejectingClaim(null);
                setRejectReason('');
                loadBroadcasts(Math.floor(first / ITEMS_PER_PAGE) + 1);
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: result.message || 'Failed to reject claim', life: 3000 });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to reject claim.', life: 3000 });
        } finally {
            setRejecting(false);
        }
    };

    const openWithdrawDialog = (claim) => {
        setWithdrawingClaim(claim);
        setWithdrawDialogVisible(true);
    };

    const submitWithdraw = async () => {
        if (!withdrawingClaim) return;

        setWithdrawing(true);
        try {
            const result = await leadBroadcastService.withdrawClaim(withdrawingClaim.id);

            if (result.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Withdrawn',
                    detail: 'Your claim has been withdrawn.',
                    life: 3000,
                });
                setWithdrawDialogVisible(false);
                const page = Math.floor(first / ITEMS_PER_PAGE) + 1;
                loadBroadcasts(page);
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: result.message || 'Failed to withdraw claim',
                    life: 4000,
                });
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to withdraw. Please try again.',
                life: 4000,
            });
        } finally {
            setWithdrawing(false);
        }
    };

    // Filtering is server-side; broadcasts already reflects active filters
    const filteredBroadcasts = broadcasts;
    const hasActiveFilters = filter !== 'all' || search || dateFrom || dateTo;

    // ══════════════════════════════════════════
    // RENDER HELPERS
    // ══════════════════════════════════════════

    /** Row: label left, value right — same pattern as OffersComparison.js */
    /** Single field in the lead info grid — compact */
    const renderLeadField = (label, value, isMasked = false, valueClass = '') => (
        <div className="col-6 md:col-3 mb-1">
            <div className="text-500 mb-0" style={{ fontSize: '0.65rem' }}>
                {isMasked && <i className="pi pi-lock text-400 mr-1" style={{ fontSize: '0.55rem' }} />}
                {label}
            </div>
            <div className={`font-semibold text-900 ${valueClass}`} style={{ fontSize: '0.8rem' }}>
                {value || <span className="text-400">-</span>}
            </div>
        </div>
    );

    /** Document availability tag — compact */
    const renderDocTag = (label, available) => (
        <Tag
            value={label}
            icon={`pi ${available ? 'pi-check' : 'pi-times'}`}
            severity={available ? 'success' : 'danger'}
            style={{ fontSize: '0.6rem', padding: '0.1rem 0.3rem', opacity: available ? 1 : 0.5 }}
        />
    );

    // ══════════════════════════════════════════
    // LEAD INFO PANEL (Structured Sections)
    // ══════════════════════════════════════════

    const renderLeadInfoPanel = (broadcast) => {
        const leadData = leadDataCache[broadcast.id];
        const isLoading = leadDataLoading[broadcast.id];
        // Use cached masked flag — false only when backend confirmed full data returned
        const isMasked = leadDataMasked[broadcast.id] !== false;

        if (isLoading && !leadData) {
            return (
                <div className="surface-50 border-round p-2">
                    <div className="grid">
                        {[...Array(8)].map((_, i) => (
                            <div key={i} className="col-6 md:col-3 mb-1">
                                <Skeleton width="60%" height="0.6rem" className="mb-1" />
                                <Skeleton width="80%" height="0.8rem" />
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        if (!leadData) {
            return (
                <div className="surface-50 border-round p-2">
                    <div className="flex align-items-center justify-content-center py-2">
                        <ProgressSpinner style={{ width: '20px', height: '20px' }} />
                        <span className="ml-2 text-500 text-xs">Loading lead details...</span>
                    </div>
                </div>
            );
        }

        return (
            <div className="surface-50 border-round p-2">
                {/* Action buttons + section header inline. Stacks on mobile so the
                    button row wraps under the title instead of overflowing the card. */}
                <div className="flex flex-column gap-2 mb-2 sm:flex-row sm:align-items-center sm:justify-content-between">
                    <div className="flex align-items-center gap-1">
                        <i className="pi pi-user text-blue-500" style={{ fontSize: '0.75rem' }} />
                        <span className="font-semibold text-blue-700" style={{ fontSize: '0.7rem' }}>PERSONAL</span>
                        {isMasked && <Tag value="Masked" severity="warning" style={{ fontSize: '0.55rem', padding: '0.05rem 0.2rem' }} />}
                    </div>
                    <div className="flex flex-wrap gap-1">
                        <Button
                            label="Regenerate"
                            icon="pi pi-refresh"
                            className="p-button-outlined p-button-warning"
                            tooltip="Generate new offers"
                            tooltipOptions={{ position: 'top' }}
                            onClick={(e) => { e.stopPropagation(); setRegenBroadcast(broadcast); setRegenVisible(true); }}
                            style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem' }}
                        />
                        {/* View Payslip — only when the lead actually has uploaded payslips.
                            Available to all company members (masked + unmasked) so lenders
                            can verify payslip authenticity before deciding to claim. The
                            backend writes an audit log entry on every click. */}
                        {leadData.documents_available?.has_payslip && (
                            <Button
                                label={
                                    leadData.documents_available.payslip_count > 1
                                        ? `View ${leadData.documents_available.payslip_count} Payslips`
                                        : 'View Payslip'
                                }
                                icon="pi pi-file-pdf"
                                className="p-button-outlined p-button-info"
                                tooltip="Open the payslip viewer (no new tabs)"
                                tooltipOptions={{ position: 'top' }}
                                loading={!!payslipLoading[broadcast.id]}
                                onClick={async (e) => {
                                    e.stopPropagation();
                                    if (payslipLoading[broadcast.id]) return;

                                    // Fetch signed URLs then open inline viewer. Replaces
                                    // the old window.open-N-tabs pattern that lost 2 of 3
                                    // to popup blockers (browsers only allow one
                                    // window.open per user gesture; subsequent ones after
                                    // an await are blocked silently).
                                    setPayslipLoading(prev => ({ ...prev, [broadcast.id]: true }));
                                    try {
                                        const result = await leadBroadcastService.getBroadcastPayslipUrls(broadcast.id);
                                        if (result?.success && Array.isArray(result.data) && result.data.length > 0) {
                                            setPayslipUrls(result.data.filter(p => p?.url));
                                            setPayslipIndex(0);
                                            setPayslipViewerVisible(true);
                                        } else {
                                            toast.current?.show({
                                                severity: 'error',
                                                summary: 'Could not load payslip',
                                                detail: result?.message || 'Try again.',
                                                life: 3000,
                                            });
                                        }
                                    } catch (err) {
                                        toast.current?.show({
                                            severity: 'error',
                                            summary: 'Error',
                                            detail: err?.message || 'Could not load payslip.',
                                            life: 3000,
                                        });
                                    } finally {
                                        setPayslipLoading(prev => ({ ...prev, [broadcast.id]: false }));
                                    }
                                }}
                                style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem' }}
                            />
                        )}
                        <Button
                            label="View Lead"
                            icon="pi pi-external-link"
                            className="p-button-outlined p-button-secondary"
                            tooltip={isMasked ? 'View lead (masked)' : 'View full lead'}
                            tooltipOptions={{ position: 'top' }}
                            onClick={(e) => { e.stopPropagation(); navigate(`/app/leads/view/${broadcast.lead_id}`); }}
                            style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem' }}
                        />
                    </div>
                </div>

                <div className="grid">
                    {renderLeadField('Customer Name', leadData.name, isMasked)}
                    {renderLeadField('Phone', leadData.phone, isMasked)}
                    {renderLeadField('Email', leadData.email, isMasked)}
                    {renderLeadField('PAN', leadData.pan, isMasked)}
                </div>

                {/* Employment — inline header */}
                <div className="flex align-items-center gap-1 mt-1 mb-1 pt-1 border-top-1 surface-border">
                    <i className="pi pi-briefcase text-purple-500" style={{ fontSize: '0.75rem' }} />
                    <span className="font-semibold text-purple-700" style={{ fontSize: '0.7rem' }}>EMPLOYMENT</span>
                </div>
                <div className="grid">
                    {renderLeadField('Employment Type', leadData.employment_type)}
                    {renderLeadField('Company', leadData.company_name)}
                    {renderLeadField('City', leadData.city)}
                    {renderLeadField('State', leadData.state)}
                </div>

                {/* Financial — inline header */}
                <div className="flex align-items-center gap-1 mt-1 mb-1 pt-1 border-top-1 surface-border">
                    <i className="pi pi-indian-rupee text-green-500" style={{ fontSize: '0.75rem' }} />
                    <span className="font-semibold text-green-700" style={{ fontSize: '0.7rem' }}>FINANCIAL</span>
                </div>
                <div className="grid">
                    {renderLeadField('Net Salary', leadData.net_salary ? formatCurrency(leadData.net_salary) : null)}
                    {renderLeadField('Loan Required', leadData.loan_amount_required ? formatLoanAmount(leadData.loan_amount_required) : null)}
                    {renderLeadField('Product Type', leadData.product_type)}
                    {renderLeadField('Tenure', leadData.period ? `${leadData.period} months` : null)}
                    {renderLeadField(
                        'CIBIL Score',
                        leadData.cibil_score,
                        false,
                        leadData.cibil_score >= 700 ? 'text-green-600' : leadData.cibil_score >= 600 ? 'text-orange-600' : leadData.cibil_score ? 'text-red-600' : ''
                    )}
                </div>

                {/* Documents — inline */}
                <div className="flex align-items-center gap-1 mt-1 mb-1 pt-1 border-top-1 surface-border">
                    <i className="pi pi-file text-cyan-500" style={{ fontSize: '0.75rem' }} />
                    <span className="font-semibold text-cyan-700" style={{ fontSize: '0.7rem' }}>DOCUMENTS</span>
                </div>
                <div className="flex flex-wrap gap-1">
                    {renderDocTag('Bank Statement', leadData.documents_available?.has_bank_statement)}
                    {renderDocTag('Payslip', leadData.documents_available?.has_payslip)}
                    {renderDocTag('ITR', leadData.documents_available?.has_itr)}
                    {renderDocTag('PAN Card', leadData.documents_available?.has_pan_document)}
                    {renderDocTag('Aadhaar', leadData.documents_available?.has_aadhar_document)}
                </div>
            </div>
        );
    };

    // ══════════════════════════════════════════
    // ELIGIBILITY SUMMARY
    // ══════════════════════════════════════════

    const renderEligibilitySummary = (eligibility) => {
        if (!eligibility || !eligibility.eligible_amount) return null;

        return (
            <div className="mt-2">
                <div className="flex align-items-center gap-1 mb-1 px-2 py-1 border-round" style={{ backgroundColor: 'var(--green-50)' }}>
                    <i className="pi pi-check-circle text-green-500" style={{ fontSize: '0.75rem' }} />
                    <span className="font-semibold text-green-700" style={{ fontSize: '0.7rem' }}>ELIGIBILITY</span>
                    {eligibility.is_eligible !== undefined && eligibility.is_eligible !== null && (
                        <Tag
                            value={eligibility.is_eligible ? 'Eligible' : 'Not Eligible'}
                            severity={eligibility.is_eligible ? 'success' : 'danger'}
                            className="ml-auto"
                            style={{ fontSize: '0.55rem', padding: '0.05rem 0.2rem' }}
                        />
                    )}
                </div>
                <div className="flex flex-wrap surface-50 border-round px-2 py-1 gap-3">
                    <div className="text-center">
                        <div className="text-500" style={{ fontSize: '0.6rem' }}>Eligible Amt</div>
                        <div className="font-bold text-green-600" style={{ fontSize: '0.85rem' }}>{formatLoanAmount(eligibility.eligible_amount)}</div>
                    </div>
                    <div className="text-center">
                        <div className="text-500" style={{ fontSize: '0.6rem' }}>FOIR</div>
                        <div className="font-bold text-orange-600" style={{ fontSize: '0.85rem' }}>{eligibility.foir_percentage || '-'}%</div>
                    </div>
                    <div className="text-center">
                        <div className="text-500" style={{ fontSize: '0.6rem' }}>EMI Capacity</div>
                        <div className="font-bold text-blue-600" style={{ fontSize: '0.85rem' }}>{eligibility.emi_capacity ? formatCurrency(eligibility.emi_capacity) : '-'}</div>
                    </div>
                    <div className="text-center">
                        <div className="text-500" style={{ fontSize: '0.6rem' }}>Income</div>
                        <div className="font-bold text-900" style={{ fontSize: '0.85rem' }}>{eligibility.total_income ? formatCurrency(eligibility.total_income) : '-'}</div>
                    </div>
                    <div className="text-center">
                        <div className="text-500" style={{ fontSize: '0.6rem' }}>Obligations</div>
                        <div className="font-bold text-red-600" style={{ fontSize: '0.85rem' }}>{eligibility.total_obligations ? formatCurrency(eligibility.total_obligations) : '-'}</div>
                    </div>
                </div>
            </div>
        );
    };

    // ══════════════════════════════════════════
    // OFFER CARD (Matching OffersComparison.js)
    // ══════════════════════════════════════════


    // ══════════════════════════════════════════
    // COLLAPSED LEAD CARD (At-a-Glance Summary)
    // ══════════════════════════════════════════

    /** My Action — Owner / claim status */
    const renderMyAction = (broadcast) => {
        if (broadcast.is_own_broadcast) return <span className="text-xs font-semibold text-blue-600">Owner</span>;
        const s = broadcast.my_claim_status || 'none';
        if (s === 'approved') return <span className="text-xs font-semibold text-green-600">Approved</span>;
        if (s === 'pending') return <span className="text-xs font-semibold text-orange-600">Pending</span>;
        if (s === 'rejected') return <span className="text-xs font-semibold text-red-600">Rejected</span>;
        if (s === 'withdrawn') return <span className="text-xs font-semibold text-500">Withdrawn</span>;
        return <span className="text-xs text-300">—</span>;
    };

    /** Attention — competition/activity info */
    const renderAttention = (broadcast) => {
        const rejectedOffers = broadcast.rejected_offers_count || 0;
        const processingOffers = broadcast.processing_offers_count || 0;
        const totalClaims = getTotalClaims(broadcast);
        if (rejectedOffers > 0) return <span className="text-xs font-semibold text-red-600">{rejectedOffers} rejected</span>;
        if (processingOffers > 0) return <span className="text-xs font-semibold text-blue-600">{processingOffers} processing</span>;
        if (totalClaims > 0) return <span className="text-xs text-500">{totalClaims} claim{totalClaims !== 1 ? 's' : ''}</span>;
        return <span className="text-xs text-300">—</span>;
    };

    const renderCollapsedCard = (broadcast) => {
        // Lead status config (unified status system)
        const leadStatusConfig = getProcessingStatusConfig(broadcast.lead_status);

        // Grid style matching column header
        const gridStyle = {
            display: 'grid',
            gridTemplateColumns: '1fr 6.5rem 5rem 2.5rem 4.5rem 8.5rem 4.5rem 5rem 1.2rem',
            alignItems: 'center',
            gap: '0 0.5rem',
        };
        const mobileGridStyle = {
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gap: '0.25rem 0.5rem',
        };

        return (
            <div
                key={broadcast.id}
                className="cursor-pointer hover:surface-hover transition-colors transition-duration-150 border-bottom-1 surface-border"
                onClick={() => toggleExpand(broadcast.id)}
            >
                {/* Desktop row */}
                <div className="hidden md:block">
                <div className="px-3 py-2" style={gridStyle}>
                    {/* Lead */}
                    <div className="flex align-items-center gap-2" style={{ minWidth: 0 }}>
                        <span className="font-semibold text-900 text-sm white-space-nowrap">{broadcast.lead_id_formatted || `Lead #${broadcast.lead_id}`}</span>
                        {(() => {
                            const ageInfo = getOffersAge(broadcast);
                            return ageInfo && ageInfo.severity !== 'success' ? (
                                <Tag severity={ageInfo.severity} value={ageInfo.label} className="text-xs" style={{ fontSize: '0.6rem', padding: '0.05rem 0.25rem' }} />
                            ) : null;
                        })()}
                    </div>
                    {/* Product */}
                    <div className="flex justify-content-center" style={{ overflow: 'hidden' }}>
                        <span className="text-xs font-medium text-700 white-space-nowrap" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {getProductLabel(broadcast.product_type)}
                        </span>
                    </div>
                    {/* Amount */}
                    <span className="text-sm font-semibold text-blue-600 text-right">
                        {broadcast.loan_amount ? formatLoanAmount(broadcast.loan_amount) : broadcast.best_emi ? formatCurrency(broadcast.best_emi) : '—'}
                    </span>
                    {/* Offers */}
                    <span className="text-xs text-500 text-center">{broadcast.total_offers || 0}</span>
                    {/* By */}
                    <div className="flex align-items-center justify-content-center" style={{ overflow: 'hidden' }}>
                        <span className="text-xs text-700" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {broadcast.broadcast_by || '—'}
                        </span>
                    </div>
                    {/* Lead Status */}
                    <div className="flex justify-content-center" style={{ overflow: 'hidden' }}>
                        <Tag value={leadStatusConfig.label} severity={leadStatusConfig.severity} icon={`pi ${leadStatusConfig.icon}`} className="text-xs" style={{ fontSize: '0.65rem', padding: '0.1rem 0.35rem', maxWidth: '100%', whiteSpace: 'nowrap' }} />
                    </div>
                    {/* My Action */}
                    <div className="flex justify-content-end">
                        {renderMyAction(broadcast)}
                    </div>
                    {/* Attention */}
                    <div className="flex justify-content-end">
                        {renderAttention(broadcast)}
                    </div>
                    {/* Chevron */}
                    <i className="pi pi-chevron-down text-400 text-xs text-right" />
                </div>
                </div>

                {/* Mobile row — card-like touch-friendly layout */}
                <div className="block md:hidden px-3 py-3" style={mobileGridStyle}>
                    <div style={{ minWidth: 0 }}>
                        <div className="flex align-items-center gap-2 mb-1">
                            <span className="font-semibold text-900" style={{ fontSize: '0.85rem' }}>{broadcast.lead_id_formatted || `Lead #${broadcast.lead_id}`}</span>
                            <span className="text-xs text-600 font-medium">{getProductLabel(broadcast.product_type)}</span>
                        </div>
                        <div className="flex align-items-center gap-2 flex-wrap">
                            {broadcast.loan_amount ? (
                                <span className="font-semibold text-blue-600" style={{ fontSize: '0.8rem' }}>{formatLoanAmount(broadcast.loan_amount)}</span>
                            ) : broadcast.best_emi ? (
                                <span className="font-semibold text-blue-600" style={{ fontSize: '0.8rem' }}>{formatCurrency(broadcast.best_emi)}</span>
                            ) : null}
                            <span className="text-500" style={{ fontSize: '0.75rem' }}>{broadcast.total_offers || 0} offers</span>
                            {broadcast.broadcast_by && (
                                <span className="text-600" style={{ fontSize: '0.75rem' }}>
                                    <i className="pi pi-user text-400 mr-1" style={{ fontSize: '0.65rem' }} />{broadcast.broadcast_by}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-column align-items-end gap-1 pl-2">
                        <Tag value={leadStatusConfig.label} severity={leadStatusConfig.severity} icon={`pi ${leadStatusConfig.icon}`} className="text-xs" style={{ fontSize: '0.65rem', padding: '0.15rem 0.35rem' }} />
                        {renderMyAction(broadcast)}
                        {renderAttention(broadcast)}
                        <i className="pi pi-chevron-down text-400" style={{ fontSize: '0.7rem' }} />
                    </div>
                </div>
            </div>
        );
    };

    // ══════════════════════════════════════════
    // EXPANDED BROADCAST CARD
    // ══════════════════════════════════════════

    // ══════════════════════════════════════════
    // OFFERS SLIDER (Sliding Navigation)
    // ══════════════════════════════════════════

    const getOfferStart = (broadcastId) => offerStartIndex[broadcastId] || 0;

    const handleOfferNav = (broadcastId, direction, totalOffers) => {
        setOfferStartIndex((prev) => {
            const current = prev[broadcastId] || 0;
            if (isMobile) {
                // Mobile: 1 at a time
                const next = direction === 'next'
                    ? Math.min(current + 1, totalOffers - 1)
                    : Math.max(current - 1, 0);
                return { ...prev, [broadcastId]: next };
            }
            // Desktop: slide by MAX_VISIBLE_OFFERS
            const next = direction === 'next'
                ? Math.min(current + MAX_VISIBLE_OFFERS, totalOffers - MAX_VISIBLE_OFFERS)
                : Math.max(current - MAX_VISIBLE_OFFERS, 0);
            return { ...prev, [broadcastId]: next };
        });
    };

    const renderOffersSlider = (broadcast, offers) => {
        if (!offers || offers.length === 0) return null;

        // Build header renderer — claim info + status badge at top of card
        const offerHeaderRenderer = (offer) => {
            const enriched = getEnriched(broadcast);
            const offerStatuses = enriched.offer_statuses || {};
            const offerStatusData = offerStatuses[offer.offer_id];
            const offerProcessingStatus = offerStatusData?.status || 'available';
            const claimCount = getOfferClaimCount(broadcast, offer.offer_id);
            const myClaimOnThis = getMyClaimOnOffer(broadcast, offer.offer_id);

            const hasStatusBadge = offerStatusData && offerProcessingStatus !== 'available';

            return (
                <div className="mb-1">
                    {/* Claims info — inline badges */}
                    <div className="flex align-items-center gap-2">
                        <span className="text-xs font-semibold text-white px-2 py-1 border-round" style={{ backgroundColor: '#4FC3F7' }}>
                            <i className="pi pi-users mr-1" style={{ fontSize: '0.7rem' }} />
                            {claimCount} Claim{claimCount !== 1 ? 's' : ''}
                        </span>
                        {myClaimOnThis && (
                            <span className="text-xs font-semibold text-white px-2 py-1 border-round" style={{ backgroundColor: '#66BB6A' }}>
                                <i className="pi pi-check-circle mr-1" style={{ fontSize: '0.7rem' }} />You claimed
                            </span>
                        )}
                    </div>
                    {hasStatusBadge && (
                        <div className="mt-1">
                            <OfferStatusBadge offerStatus={offerStatusData} />
                        </div>
                    )}
                </div>
            );
        };

        // Toggle the verified flag on a single offer. Mirrors handleEditOffer's
        // cache-patching shape so the UI updates without a full refetch.
        const handleToggleVerify = async (offer) => {
            const offerId = offer.offer_id || offer.id;
            if (!offerId) return;
            const next = !offer.is_verified;
            try {
                const result = await leadBroadcastService.verifyBroadcastOffer(broadcast.id, offerId, next);
                if (!result?.success) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Verify failed',
                        detail: result?.message || 'Could not update verification.',
                        life: 3500,
                    });
                    return;
                }
                const saved = result.data?.offer || { ...offer, is_verified: next };
                setBroadcastDetailsCache((prev) => {
                    const current = prev[broadcast.id] || {};
                    const patchList = (list) => {
                        if (!Array.isArray(list)) return list;
                        return list.map((o) => {
                            const id = o.offer_id || o.id;
                            return id === offerId ? { ...o, ...saved } : o;
                        });
                    };
                    const updated = { ...current };
                    if (Array.isArray(updated.offers)) updated.offers = patchList(updated.offers);
                    if (updated.broadcast_offers?.offers) {
                        updated.broadcast_offers = {
                            ...updated.broadcast_offers,
                            offers: patchList(updated.broadcast_offers.offers),
                        };
                    }
                    return { ...prev, [broadcast.id]: updated };
                });
                toast.current?.show({
                    severity: 'success',
                    summary: next ? 'Marked as verified' : 'Verification removed',
                    life: 2000,
                });
            } catch (err) {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Verify failed',
                    detail: err?.message || 'Could not update verification.',
                    life: 3500,
                });
            }
        };

        // Build footer renderer for each offer — action buttons + claims.
        // `view` is 'cards' (narrow lane → stack full-width) or 'table'
        // (wide row → compact auto-width buttons, left-aligned).
        const offerFooterRenderer = (offer, view = 'cards') => {
            const isTable = view === 'table';
            const enriched = getEnriched(broadcast);
            const myClaimOnThis = getMyClaimOnOffer(broadcast, offer.offer_id);
            const offerStatuses = enriched.offer_statuses || {};
            const offerStatusData = offerStatuses[offer.offer_id];
            const offerProcessingStatus = offerStatusData?.status || 'available';
            const isOfferBlocked = ['rejected', 'bank_rejected', 'customer_rejected', 'processor_rejected'].includes(offerProcessingStatus) || offerProcessingStatus === 'in_processing' || offerProcessingStatus === 'processing' || offerProcessingStatus === 'approved' || offerProcessingStatus === 'sanction' || offerProcessingStatus === 'disbursed' || offerProcessingStatus === 'disbursal';
            const isThisSelectedOffer = isSelectedOffer(broadcast, offer.offer_id);
            const offerClaims = isOwnBroadcast(broadcast)
                ? (enriched.all_claims || []).filter(c => String(c.offer_id) === String(offer.offer_id))
                : [];

            // "Updated by ..." attribution now lives in the shared
            // OffersComparison component (above the renderOfferFooter slot)
            // so both this page and the lead wizard show the same line.

            const canVerify = broadcast?.status !== 'cancelled';
            const isVerified = !!offer.is_verified;

            // Right side of the action row — the existing claim/status logic
            // (selected → Assigned tag; own broadcast → nothing; my claim → status
            // tag + withdraw; offer blocked → status badge; else → Claim button).
            const claimSlot = (
                isThisSelectedOffer ? (
                    <div className={`flex align-items-center gap-2 ${isTable ? '' : 'justify-content-between w-full'}`}>
                        <div className="flex align-items-center gap-2">
                            <i className="pi pi-user text-blue-500" />
                            <span className="text-sm font-semibold text-blue-700">
                                {getProcessorName(broadcast)}
                            </span>
                        </div>
                        <Tag value="Assigned" severity="info" icon="pi pi-check" className="text-xs" />
                    </div>
                ) : isOwnBroadcast(broadcast) ? null
                : myClaimOnThis ? (
                    <div className={`flex align-items-center gap-2 ${isTable ? '' : 'justify-content-between w-full'}`}>
                        <Tag
                            value={getClaimStatusConfig(myClaimOnThis.status).label}
                            severity={getClaimStatusConfig(myClaimOnThis.status).severity}
                            icon={`pi ${getClaimStatusConfig(myClaimOnThis.status).icon}`}
                        />
                        {myClaimOnThis.status === 'pending' && (
                            <Button
                                icon="pi pi-undo"
                                aria-label="Withdraw Claim"
                                className="p-button-text p-button-sm p-button-danger"
                                tooltip="Withdraw Claim"
                                tooltipOptions={{ position: 'top' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openWithdrawDialog(myClaimOnThis);
                                }}
                            />
                        )}
                    </div>
                ) : isOfferBlocked ? (
                    <div className={isTable ? '' : 'w-full text-center'}>
                        <OfferStatusBadge offerStatus={offerStatusData} compact />
                    </div>
                ) : (
                    <Button
                        label={isProcessorSelected(broadcast) ? 'Claim (Alternative)' : 'Claim'}
                        icon="pi pi-hand-point-up"
                        className={`p-button-sm ${isTable ? '' : 'w-full'} ${isProcessorSelected(broadcast) ? 'p-button-outlined' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            openClaimDialog(broadcast, offer);
                        }}
                    />
                )
            );

            return (
                <>
                    {/* Action Footer: Claim/status (left) + Verify (right).
                        - Table view (wide row): compact, auto-width buttons on a
                          single line, left-aligned. Buttons size to their label.
                        - Cards view (narrow lane): each slot flex-1 with a
                          min-width so they wrap & stack full-width when tight,
                          so "Verify" never truncates. */}
                    <div className={`flex flex-wrap gap-2 mt-2 pt-2 border-top-1 surface-border ${isTable ? 'align-items-center' : ''}`}>
                        {claimSlot !== null && (
                            <div className={isTable ? 'flex-none' : (canVerify ? 'flex-1' : 'w-full')} style={!isTable && canVerify ? { minWidth: '8rem' } : undefined}>{claimSlot}</div>
                        )}
                        {canVerify && (
                            <div className={isTable ? 'flex-none' : (claimSlot !== null ? 'flex-1' : 'w-full')} style={!isTable && claimSlot !== null ? { minWidth: '8rem' } : undefined}>
                                <Button
                                    label={isVerified ? 'Verified' : 'Verify'}
                                    icon={isVerified ? 'pi pi-check-circle' : 'pi pi-shield'}
                                    severity={isVerified ? 'success' : 'secondary'}
                                    outlined={!isVerified}
                                    size="small"
                                    className={isTable ? '' : 'w-full'}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleVerify(offer);
                                    }}
                                    tooltip={isVerified ? 'Click to remove verification' : 'Mark this offer as verified by you'}
                                    tooltipOptions={{ position: 'top' }}
                                />
                            </div>
                        )}
                    </div>

                    {/* Claims Section — own broadcast, matches ReceivedClaims design */}
                    {isOwnBroadcast(broadcast) && (
                        <>
                            <Divider className="my-3" />
                            <div className="flex align-items-center gap-2 mb-2 p-2 border-round" style={{ backgroundColor: 'var(--yellow-50)' }}>
                                <i className="pi pi-users text-yellow-700" />
                                <span className="font-semibold text-yellow-800 text-sm">
                                    CLAIMS ({offerClaims.length})
                                </span>
                            </div>
                            {offerClaims.length === 0 ? (
                                <div className="flex align-items-center gap-2 text-500 text-xs py-2 px-1">
                                    <i className="pi pi-info-circle" />
                                    <span>No claims yet</span>
                                </div>
                            ) : (
                                <div className="border-1 surface-border border-round">
                                    {offerClaims.map((claim, idx) => {
                                        const isApproved = claim.status === 'approved';
                                        const isCurrentProcessor = isApproved && broadcast.selected_offer_id === claim.offer_id;
                                        const isBroadcastProcessorSelected = broadcast.status === 'processor_selected';
                                        const isRetryNeeded = broadcast.processing_status === 'retry_needed';
                                        const isPending = claim.status === 'pending';

                                        const claimOfferStatus = (getEnriched(broadcast).offer_statuses || {})[offer.offer_id];
                                        const isOfferRejected = ['rejected', 'bank_rejected', 'customer_rejected', 'processor_rejected'].includes(claimOfferStatus?.status);
                                        const isOfferLocked = isOfferRejected && isCurrentProcessor;

                                        const canMakeProcessor = (isBroadcastProcessorSelected && !isCurrentProcessor && !isOfferLocked)
                                            || (isRetryNeeded && !isCurrentProcessor && !isOfferLocked && claim.status !== 'rejected' && claim.status !== 'withdrawn');

                                        return (
                                            <div
                                                key={idx}
                                                className="p-3 border-bottom-1 surface-border"
                                                style={isCurrentProcessor && !isOfferLocked ? { backgroundColor: 'var(--green-50, #ecfdf5)' } : isOfferLocked ? { backgroundColor: 'var(--red-50)', opacity: 0.6 } : {}}
                                            >
                                                <div className="flex flex-wrap align-items-start justify-content-between gap-2">
                                                    <div className="flex align-items-start gap-2 flex-grow-1">
                                                        <i className={`pi ${isCurrentProcessor ? 'pi-star-fill text-green-500' : 'pi-user'} text-lg mt-1`} style={{ minWidth: '1.25rem' }} />
                                                        <div>
                                                            <div className="flex align-items-center gap-2 flex-wrap">
                                                                <span className="font-semibold text-900">{claim.claimed_by?.name || 'Unknown'}</span>
                                                                <Tag
                                                                    value={getClaimStatusConfig(claim.status).label}
                                                                    severity={getClaimStatusConfig(claim.status).severity}
                                                                    icon={`pi ${getClaimStatusConfig(claim.status).icon}`}
                                                                    className="text-xs"
                                                                />
                                                                {isCurrentProcessor && <Tag value="Processor" severity="success" className="text-xs" />}
                                                                {isOfferLocked && <Tag value="Offer Rejected" severity="danger" icon="pi pi-lock" className="text-xs" />}
                                                            </div>
                                                            <div className="text-500 text-xs mt-1">
                                                                Claimed {claim.created_at || 'recently'}
                                                            </div>
                                                            {claim.status === 'rejected' && claim.rejected_reason && <div className="text-red-600 text-xs mt-1">Reason: {claim.rejected_reason}</div>}
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap gap-2 align-items-center flex-shrink-0">
                                                        {isPending && !isBroadcastProcessorSelected && !isRetryNeeded && (
                                                            <>
                                                                <Button label="Approve" icon="pi pi-check" className="p-button-success p-button-outlined p-button-sm" onClick={(e) => { e.stopPropagation(); openApproveDialog(claim); }} />
                                                                <Button label="Reject" icon="pi pi-times" className="p-button-danger p-button-text p-button-sm" onClick={(e) => { e.stopPropagation(); openRejectDialog(claim); }} />
                                                            </>
                                                        )}
                                                        {isRetryNeeded && !isOfferLocked && !isCurrentProcessor && isPending && (
                                                            <>
                                                                <Button label="Make Processor" icon="pi pi-sync" className="p-button-warning p-button-sm" onClick={(e) => { e.stopPropagation(); setProcessorConfirmTarget({ claim, broadcast }); }} />
                                                                <Button label="Reject" icon="pi pi-times" className="p-button-danger p-button-text p-button-sm" onClick={(e) => { e.stopPropagation(); openRejectDialog(claim); }} />
                                                            </>
                                                        )}
                                                        {canMakeProcessor && !isRetryNeeded && !isOfferLocked && (
                                                            <Button label="Make Processor" icon="pi pi-sync" className="p-button-warning p-button-outlined p-button-sm" onClick={(e) => { e.stopPropagation(); setProcessorConfirmTarget({ claim, broadcast }); }} />
                                                        )}
                                                    </div>
                                                </div>
                                                {claim.claim_message && (
                                                    <div className="mt-2 ml-5 p-2 border-round surface-100" style={{ borderLeft: '3px solid var(--primary-color)' }}>
                                                        <span className="text-700 text-xs font-italic">"{claim.claim_message}"</span>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}
                </>
            );
        };

        // Cancelled broadcasts shouldn't allow edits — keeps the audit trail
        // clean. Everything else is editable by any company member; the
        // backend enforces the same gate so a stale UI state can't smuggle
        // an edit through.
        const canEdit = broadcast?.status !== 'cancelled';

        const handleEditOffer = async (index, updatedOffer) => {
            const offerId = updatedOffer.offer_id || updatedOffer.id;
            if (!offerId) {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Cannot save',
                    detail: 'This offer has no ID.',
                    life: 3000,
                });
                return;
            }
            try {
                const result = await leadBroadcastService.updateBroadcastOffer(
                    broadcast.id,
                    offerId,
                    updatedOffer
                );
                if (!result?.success) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Save failed',
                        detail: result?.message || 'Could not save the offer changes.',
                        life: 4000,
                    });
                    return;
                }

                // Patch the local cache so the edited offer (and the new
                // best_rate / best_emi) render immediately without a full
                // refetch. Both shapes (`enriched.offers` and
                // `enriched.broadcast_offers.offers`) need to stay in sync
                // because getOffers() reads whichever exists first.
                const saved = result.data?.offer || updatedOffer;
                setBroadcastDetailsCache((prev) => {
                    const current = prev[broadcast.id] || {};
                    const patchList = (list) => {
                        if (!Array.isArray(list)) return list;
                        return list.map((o) => {
                            const id = o.offer_id || o.id;
                            return id === offerId ? { ...o, ...saved } : o;
                        });
                    };
                    const next = { ...current };
                    if (Array.isArray(next.offers)) next.offers = patchList(next.offers);
                    if (next.broadcast_offers?.offers) {
                        next.broadcast_offers = {
                            ...next.broadcast_offers,
                            offers: patchList(next.broadcast_offers.offers),
                            best_rate: result.data?.best_rate ?? next.broadcast_offers.best_rate,
                            best_emi: result.data?.best_emi ?? next.broadcast_offers.best_emi,
                        };
                    }
                    return { ...prev, [broadcast.id]: next };
                });

                toast.current?.show({
                    severity: 'success',
                    summary: 'Offer updated',
                    detail: result.data?.changed_fields?.length
                        ? `${result.data.changed_fields.length} field${result.data.changed_fields.length === 1 ? '' : 's'} updated`
                        : 'Saved',
                    life: 2500,
                });
            } catch (err) {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Save failed',
                    detail: err?.message || 'Could not save the offer changes.',
                    life: 4000,
                });
            }
        };

        return (
            <div className="mt-2">
                <OffersComparison
                    offers={offers}
                    selectedOffer={broadcast.selected_offer_id ? { offer_id: broadcast.selected_offer_id } : null}
                    readOnly={!canEdit}
                    hideEligibility={false}
                    renderOfferHeader={offerHeaderRenderer}
                    renderOfferFooter={offerFooterRenderer}
                    onEditOffer={canEdit ? handleEditOffer : null}
                    stickyHeaderTop="4rem"
                />
            </div>
        );
    };

    // ══════════════════════════════════════════
    // EXPANDED BROADCAST CARD
    // ══════════════════════════════════════════

    const renderExpandedCard = (broadcast) => {
        const offers = getSortedOffers(broadcast);

        return (
            <div
                key={broadcast.id}
                className="surface-card border-round shadow-1 mb-2 overflow-hidden"
                style={{ borderLeft: `3px solid ${isProcessorSelected(broadcast) ? 'var(--blue-500)' : 'var(--primary-color)'}` }}
            >
                {/* Compact Header — mirrors collapsed row style, click to collapse */}
                <div
                    className="cursor-pointer hover:surface-hover transition-colors transition-duration-150 border-bottom-1 surface-border"
                    onClick={() => toggleExpand(broadcast.id)}
                >
                    {/* Desktop header */}
                    <div className="hidden md:block">
                        <div className="px-3 py-2" style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 6.5rem 5rem 2.5rem 4.5rem 8.5rem 4.5rem 5rem 1.2rem',
                            alignItems: 'center',
                            gap: '0 0.5rem',
                        }}>
                            <div className="flex align-items-center gap-2" style={{ minWidth: 0 }}>
                                <span className="font-semibold text-900 text-sm white-space-nowrap">{broadcast.lead_id_formatted || `Lead #${broadcast.lead_id}`}</span>
                            </div>
                            <div className="flex justify-content-center">
                                <span className="text-xs font-medium text-700 white-space-nowrap">{getProductLabel(broadcast.product_type)}</span>
                            </div>
                            <span className="text-sm font-semibold text-blue-600 text-right">
                                {broadcast.loan_amount ? formatLoanAmount(broadcast.loan_amount) : broadcast.best_emi ? formatCurrency(broadcast.best_emi) : '—'}
                            </span>
                            <span className="text-xs text-500 text-center">{broadcast.total_offers || 0}</span>
                            <div className="flex align-items-center justify-content-center" style={{ overflow: 'hidden' }}>
                                <span className="text-xs text-700" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {broadcast.broadcast_by || '—'}
                                </span>
                            </div>
                            <div className="flex justify-content-center" style={{ overflow: 'hidden' }}>
                                {(() => {
                                    const lsc = getProcessingStatusConfig(broadcast.lead_status);
                                    return <Tag value={lsc.label} severity={lsc.severity} icon={`pi ${lsc.icon}`} className="text-xs" style={{ fontSize: '0.65rem', padding: '0.1rem 0.35rem', maxWidth: '100%', whiteSpace: 'nowrap' }} />;
                                })()}
                            </div>
                            <div className="flex justify-content-end">
                                {renderMyAction(broadcast)}
                            </div>
                            <div className="flex justify-content-end">
                                {renderAttention(broadcast)}
                            </div>
                            <i className="pi pi-chevron-up text-400 text-xs text-right" />
                        </div>
                    </div>
                    {/* Mobile header */}
                    <div className="block md:hidden px-3 py-3" style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.25rem 0.5rem' }}>
                        <div style={{ minWidth: 0 }}>
                            <div className="flex align-items-center gap-2 mb-1">
                                <span className="font-semibold text-900" style={{ fontSize: '0.85rem' }}>{broadcast.lead_id_formatted || `Lead #${broadcast.lead_id}`}</span>
                                <span className="text-xs text-600 font-medium">{getProductLabel(broadcast.product_type)}</span>
                            </div>
                            <div className="flex align-items-center gap-2 flex-wrap">
                                {broadcast.loan_amount && <span className="font-semibold text-blue-600" style={{ fontSize: '0.8rem' }}>{formatLoanAmount(broadcast.loan_amount)}</span>}
                                <span className="text-500" style={{ fontSize: '0.75rem' }}>{broadcast.total_offers || 0} offers</span>
                                <span className="text-500" style={{ fontSize: '0.75rem' }}>{broadcast.broadcast_by}</span>
                            </div>
                        </div>
                        <div className="flex flex-column align-items-end gap-1 pl-2">
                            {(() => {
                                const lsc = getProcessingStatusConfig(broadcast.lead_status);
                                return <Tag value={lsc.label} severity={lsc.severity} icon={`pi ${lsc.icon}`} className="text-xs" style={{ fontSize: '0.65rem', padding: '0.15rem 0.35rem' }} />;
                            })()}
                            {renderMyAction(broadcast)}
                            {renderAttention(broadcast)}
                            <i className="pi pi-chevron-up text-400" style={{ fontSize: '0.7rem' }} />
                        </div>
                    </div>
                </div>

                {/* Expanded Content — compact info strip + processor banner */}
                <div className="px-3 py-2">
                    {/* Processor Assignment — compact inline */}
                    {isProcessorSelected(broadcast) && (
                        <div className="flex align-items-center gap-2 px-2 py-2 mb-2 border-round" style={{ backgroundColor: 'var(--blue-50)' }}>
                            <i className="pi pi-user text-blue-600" style={{ fontSize: '0.85rem' }} />
                            <span className="text-sm font-semibold text-blue-800">{getProcessorName(broadcast)}</span>
                            <span className="text-xs text-blue-600">{getSelectedAt(broadcast)}</span>
                            {!isOwnBroadcast(broadcast) && <Tag value="Assigned" severity="info" className="text-xs ml-auto" style={{ fontSize: '0.6rem', padding: '0.05rem 0.25rem' }} />}
                        </div>
                    )}

                    {/* Regeneration info — compact inline */}
                    {getEnriched(broadcast).regeneration_history?.length > 1 && (() => {
                        const regenHistory = getEnriched(broadcast).regeneration_history;
                        return (
                            <div className="flex align-items-center gap-2 px-2 py-1 mb-2 border-round" style={{ backgroundColor: 'var(--yellow-50)' }}>
                                <i className="pi pi-refresh text-yellow-600" style={{ fontSize: '0.75rem' }} />
                                <span className="text-xs text-700">
                                    Updated {regenHistory.length - 1}x — {regenHistory[regenHistory.length - 1]?.generated_by_name},
                                    {' '}{new Date(regenHistory[regenHistory.length - 1]?.generated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                </span>
                            </div>
                        );
                    })()}

                    {/* Lead Info + Eligibility — side by side on desktop, stacked on mobile */}
                    {renderLeadInfoPanel(broadcast)}
                    {getEnriched(broadcast).eligibility && renderEligibilitySummary(getEnriched(broadcast).eligibility)}

                    {/* Offers — Sliding Navigation */}
                    {renderOffersSlider(broadcast, offers)}
                </div>
            </div>
        );
    };

    // ══════════════════════════════════════════
    // MAIN RENDER
    // ══════════════════════════════════════════

    return (
        <div className="grid available-offers">
            <div className="col-12">
                <Toast ref={toast} />

                {/* Page Header - Compact */}
                <div className="surface-card border-round shadow-1 p-2 mb-2">
                    <div className="flex align-items-center justify-content-between">
                        <h2 className="text-900 font-bold m-0" style={{ fontSize: '1rem' }}>Available Offers</h2>
                    </div>
                </div>

                {/* Summary stats - Compact */}
                {!loading && totalRecords > 0 && (
                    <div className="flex flex-wrap align-items-center gap-2 mb-2">
                        <span className="flex align-items-center gap-1 text-xs text-600 surface-100 border-round px-2 py-1">
                            <i className="pi pi-list" style={{ fontSize: '0.7rem' }} /> Total <strong>{totalRecords}</strong>
                        </span>
                        {broadcasts.reduce((sum, b) => sum + getMyClaimsCount(b), 0) > 0 && (
                            <span className="flex align-items-center gap-1 text-xs border-round px-2 py-1" style={{ backgroundColor: 'var(--green-50, #ecfdf5)', color: 'var(--green-700)' }}>
                                <i className="pi pi-check-circle" style={{ fontSize: '0.7rem' }} /> My Claims <strong>{broadcasts.reduce((sum, b) => sum + getMyClaimsCount(b), 0)}</strong>
                            </span>
                        )}
                        {broadcasts.filter(b => b.status === 'processor_selected').length > 0 && (
                            <span className="flex align-items-center gap-1 text-xs border-round px-2 py-1" style={{ backgroundColor: 'var(--blue-50, #eff6ff)', color: 'var(--blue-700)' }}>
                                <i className="pi pi-user" style={{ fontSize: '0.7rem' }} /> Assigned <strong>{broadcasts.filter(b => b.status === 'processor_selected').length}</strong>
                            </span>
                        )}
                    </div>
                )}

                {/* Filters — Desktop: inline flex row | Mobile: stacked full-width */}
                <div className="surface-card border-round shadow-1 p-2 mb-2">
                    {/* Desktop filters */}
                    <div className="hidden md:flex align-items-end gap-2 flex-wrap">
                        <div className="flex-1" style={{ minWidth: '120px', maxWidth: '180px' }}>
                            <label className="block text-500 mb-1" style={{ fontSize: '0.87rem' }}>Search</label>
                            <span className="p-input-icon-left w-full">
                                <i className="pi pi-search" style={{ fontSize: '0.7rem' }} />
                                <InputText
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
                                    placeholder="Lead ID or name..."
                                    className="w-full p-inputtext-sm"
                                    style={{ fontSize: '0.7rem', paddingLeft: '2rem' }}
                                />
                            </span>
                        </div>
                        <div className="flex-1" style={{ minWidth: '100px', maxWidth: '130px' }}>
                            <label className="block text-500 mb-1" style={{ fontSize: '0.87rem' }}>Status</label>
                            <Dropdown
                                value={filter}
                                options={FILTER_OPTIONS}
                                onChange={(e) => { setFilter(e.value); setFirst(0); setExpandedBroadcasts({}); }}
                                showClear={filter !== 'all'}
                                className="w-full p-inputtext-sm"
                                panelStyle={{ fontSize: '0.7rem' }}
                                pt={{ input: { style: { fontSize: '0.7rem' } }, item: { style: { fontSize: '0.7rem' } } }}
                            />
                        </div>
                        <div className="flex-1" style={{ minWidth: '100px', maxWidth: '150px' }}>
                            <label className="block text-500 mb-1" style={{ fontSize: '0.87rem' }}>Product</label>
                            <Dropdown
                                value={loanTypeFilter}
                                options={loanTypes.map(lt => ({ label: lt.name, value: lt.id }))}
                                onChange={(e) => { setLoanTypeFilter(e.value); setFirst(0); setExpandedBroadcasts({}); }}
                                placeholder="All Products"
                                showClear={!!loanTypeFilter}
                                className="w-full p-inputtext-sm"
                                panelStyle={{ fontSize: '0.7rem' }}
                                pt={{ input: { style: { fontSize: '0.7rem' } }, item: { style: { fontSize: '0.7rem' } } }}
                            />
                        </div>
                        <div className="flex-1" style={{ minWidth: '120px', maxWidth: '160px' }}>
                            <label className="block text-500 mb-1" style={{ fontSize: '0.87rem' }}>From Date</label>
                            <Calendar
                                value={dateFrom}
                                onChange={(e) => { setDateFrom(e.value); setFirst(0); setExpandedBroadcasts({}); }}
                                placeholder="From"
                                className="w-full p-inputtext-sm"
                                showIcon inputStyle={{ fontSize: '0.7rem' }}
                                dateFormat="dd M yy" showButtonBar maxDate={dateTo || new Date()}
                                pt={{ input: { style: { fontSize: '0.7rem' } }, dropdownButton: { style: { width: '2rem', padding: '0' } }, icon: { style: { fontSize: '0.75rem' } } }}
                            />
                        </div>
                        <div className="flex-1" style={{ minWidth: '120px', maxWidth: '160px' }}>
                            <label className="block text-500 mb-1" style={{ fontSize: '0.87rem' }}>To Date</label>
                            <Calendar
                                value={dateTo}
                                onChange={(e) => { setDateTo(e.value); setFirst(0); setExpandedBroadcasts({}); }}
                                placeholder="To"
                                className="w-full p-inputtext-sm"
                                showIcon inputStyle={{ fontSize: '0.7rem' }}
                                dateFormat="dd M yy" showButtonBar minDate={dateFrom || undefined} maxDate={new Date()}
                                pt={{ input: { style: { fontSize: '0.7rem' } }, dropdownButton: { style: { width: '2rem', padding: '0' } }, icon: { style: { fontSize: '0.75rem' } } }}
                            />
                        </div>
                        <div className="flex align-items-center gap-1 ml-auto flex-shrink-0">
                            <Button
                                icon="pi pi-refresh"
                                className="p-button-text p-button-rounded"
                                onClick={handleRefresh}
                                loading={loading}
                                tooltip="Refresh" tooltipOptions={{ position: 'top' }}
                                style={{ width: '1.75rem', height: '1.75rem' }}
                            />
                        </div>
                    </div>

                    {/* Mobile filters — full-width stacked layout */}
                    <div className="block md:hidden">
                        <div className="flex gap-2 mb-2">
                            <div className="flex-1">
                                <span className="p-input-icon-left w-full">
                                    <i className="pi pi-search" style={{ fontSize: '0.75rem' }} />
                                    <InputText
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
                                        placeholder="Search lead..."
                                        className="w-full p-inputtext-sm"
                                        style={{ fontSize: '0.8rem', paddingLeft: '2rem' }}
                                    />
                                </span>
                            </div>
                            <Button
                                icon="pi pi-refresh"
                                className="p-button-text p-button-rounded flex-shrink-0"
                                onClick={handleRefresh}
                                loading={loading}
                                style={{ width: '2.25rem', height: '2.25rem' }}
                            />
                        </div>
                        <div className="flex gap-2">
                            <Dropdown
                                value={filter}
                                options={FILTER_OPTIONS}
                                onChange={(e) => { setFilter(e.value); setFirst(0); setExpandedBroadcasts({}); }}
                                showClear={filter !== 'all'}
                                className="flex-1 p-inputtext-sm"
                                panelStyle={{ fontSize: '0.8rem' }}
                                pt={{ input: { style: { fontSize: '0.8rem' } }, item: { style: { fontSize: '0.8rem', padding: '0.6rem 0.75rem' } } }}
                            />
                            <Dropdown
                                value={loanTypeFilter}
                                options={loanTypes.map(lt => ({ label: lt.name, value: lt.id }))}
                                onChange={(e) => { setLoanTypeFilter(e.value); setFirst(0); setExpandedBroadcasts({}); }}
                                placeholder="All Products"
                                showClear={!!loanTypeFilter}
                                className="flex-1 p-inputtext-sm"
                                panelStyle={{ fontSize: '0.8rem' }}
                                pt={{ input: { style: { fontSize: '0.8rem' } }, item: { style: { fontSize: '0.8rem', padding: '0.6rem 0.75rem' } } }}
                            />
                        </div>
                        <div className="flex gap-2 mt-2">
                            <Calendar
                                value={dateFrom}
                                onChange={(e) => { setDateFrom(e.value); setFirst(0); setExpandedBroadcasts({}); }}
                                placeholder="From"
                                className="flex-1 p-inputtext-sm"
                                showIcon inputStyle={{ fontSize: '0.8rem' }}
                                dateFormat="dd M" showButtonBar maxDate={dateTo || new Date()}
                                pt={{ input: { style: { fontSize: '0.8rem' } }, dropdownButton: { style: { width: '2.25rem', padding: '0' } } }}
                            />
                            <Calendar
                                value={dateTo}
                                onChange={(e) => { setDateTo(e.value); setFirst(0); setExpandedBroadcasts({}); }}
                                placeholder="To"
                                className="flex-1 p-inputtext-sm"
                                showIcon inputStyle={{ fontSize: '0.8rem' }}
                                dateFormat="dd M" showButtonBar minDate={dateFrom || undefined} maxDate={new Date()}
                                pt={{ input: { style: { fontSize: '0.8rem' } }, dropdownButton: { style: { width: '2.25rem', padding: '0' } } }}
                            />
                        </div>
                    </div>

                    {/* Active filters & Results count */}
                    <div className="flex align-items-center justify-content-between mt-2 pt-2 border-top-1 surface-border">
                        <span className="text-500" style={{ fontSize: '0.7rem' }}>
                            {loading ? 'Loading...' : `${totalRecords} broadcast${totalRecords !== 1 ? 's' : ''}`}
                            {hasActiveFilters && ` \u2022 filters active`}
                        </span>
                        {hasActiveFilters && (
                            <Button
                                label="Clear"
                                icon="pi pi-filter-slash"
                                className="p-button-text p-button-sm"
                                onClick={handleClearFilters}
                                style={{ fontSize: '0.65rem', padding: '0.2rem 0.4rem' }}
                            />
                        )}
                    </div>
                </div>

                {/* Content */}
                {loading ? (
                    <div>
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="surface-card border-round shadow-1 p-3 mb-2">
                                <div className="flex align-items-center justify-content-between">
                                    <div className="flex align-items-center gap-3">
                                        <Skeleton shape="circle" size="2.5rem" />
                                        <div>
                                            <Skeleton width="10rem" height="1rem" className="mb-2" />
                                            <Skeleton width="15rem" height="0.75rem" />
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <Skeleton width="5rem" height="1.5rem" borderRadius="1rem" />
                                        <Skeleton width="4rem" height="1.5rem" borderRadius="1rem" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : filteredBroadcasts.length === 0 ? (
                    <div className="flex flex-column align-items-center justify-content-center py-4">
                        <i className="pi pi-megaphone text-300 mb-2" style={{ fontSize: '1.5rem' }} />
                        <div className="text-600 font-medium mb-1" style={{ fontSize: '0.85rem' }}>No broadcasts found</div>
                        <div className="text-400 text-center" style={{ maxWidth: '20rem', fontSize: '0.7rem' }}>
                            {hasActiveFilters
                                ? 'No broadcasts match your current filters.'
                                : 'No broadcasts available right now. Check back later.'}
                        </div>
                        {hasActiveFilters && (
                            <Button label="Clear" icon="pi pi-filter-slash" className="p-button-text p-button-sm mt-2" onClick={handleClearFilters} style={{ fontSize: '0.65rem', padding: '0.2rem 0.4rem' }} />
                        )}
                    </div>
                ) : (
                    <>
                        {/* Column header (desktop only) */}
                        <div className="hidden md:block">
                            <div
                                className="px-3 py-2 border-bottom-2 surface-border mb-1"
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 6.5rem 5rem 2.5rem 4.5rem 8.5rem 4.5rem 5rem 1.2rem',
                                    gap: '0 0.5rem',
                                    alignItems: 'center',
                                }}
                            >
                                <span className="text-xs font-semibold text-500 uppercase">Lead</span>
                                <span className="text-xs font-semibold text-500 uppercase text-center">Product</span>
                                <span className="text-xs font-semibold text-500 uppercase text-right">Amount</span>
                                <span className="text-xs font-semibold text-500 uppercase text-center">Offers</span>
                                <span className="text-xs font-semibold text-500 uppercase text-center">By</span>
                                <span className="text-xs font-semibold text-500 uppercase text-center">Status</span>
                                <span className="text-xs font-semibold text-500 uppercase text-right">Action</span>
                                <span className="text-xs font-semibold text-500 uppercase text-right">Attention</span>
                                <span></span>
                            </div>
                        </div>
                        {/* Group broadcasts by created_at date and inject a section
                            header whenever the bucket changes. Modelled after Chrome /
                            WhatsApp history: "Today", "Yesterday", weekday for this
                            week, then full date for older entries. Relies on the list
                            already being sorted newest-first by the backend; we don't
                            re-sort here so any DSA filter/search order is preserved. */}
                        {(() => {
                            const nodes = [];
                            let lastBucketKey = null;
                            filteredBroadcasts.forEach((broadcast) => {
                                const bucket = getBroadcastDateBucket(broadcast);
                                if (bucket.key !== lastBucketKey) {
                                    nodes.push(
                                        <div
                                            key={`date-header-${bucket.key}`}
                                            className="surface-100 border-round px-3 py-2 mt-3 mb-2 flex align-items-center gap-2"
                                            style={{ borderLeft: '3px solid var(--primary-color)' }}
                                        >
                                            <i className="pi pi-calendar text-primary text-xs" aria-hidden="true"></i>
                                            <span className="text-sm font-semibold text-800">{bucket.label}</span>
                                        </div>
                                    );
                                    lastBucketKey = bucket.key;
                                }
                                nodes.push(
                                    expandedBroadcasts[broadcast.id]
                                        ? renderExpandedCard(broadcast)
                                        : renderCollapsedCard(broadcast)
                                );
                            });
                            return nodes;
                        })()}
                    </>
                )}

                {/* Paginator — always visible, centered, marketplace-matching compact style */}
                <div className="flex justify-content-center mt-2">
                    <Paginator
                        first={first}
                        rows={ITEMS_PER_PAGE}
                        totalRecords={Math.max(totalRecords, 1)}
                        onPageChange={onPageChange}
                        template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                        className="surface-card border-round shadow-1"
                        style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem' }}
                        pt={{
                            root: { style: { padding: '0.25rem' } },
                            firstPageButton: { style: { minWidth: '1.5rem', height: '1.5rem' } },
                            previousPageButton: { style: { minWidth: '1.5rem', height: '1.5rem' } },
                            nextPageButton: { style: { minWidth: '1.5rem', height: '1.5rem' } },
                            lastPageButton: { style: { minWidth: '1.5rem', height: '1.5rem' } },
                            pageButton: { style: { minWidth: '1.5rem', height: '1.5rem', fontSize: '0.65rem' } }
                        }}
                    />
                </div>

                {/* Claim Dialog */}
                <Dialog
                    header={
                        <div className="flex align-items-center gap-2">
                            <i className="pi pi-hand-point-up text-primary" />
                            <span>Claim Offer</span>
                        </div>
                    }
                    visible={claimDialogVisible}
                    onHide={() => setClaimDialogVisible(false)}
                    style={{ width: '500px' }}
                    modal
                    footer={
                        <div className="flex justify-content-end gap-2">
                            <Button
                                label="Cancel"
                                icon="pi pi-times"
                                className="p-button-text"
                                onClick={() => setClaimDialogVisible(false)}
                                disabled={submittingClaim}
                            />
                            <Button
                                label="Submit Claim"
                                icon="pi pi-check"
                                onClick={submitClaim}
                                loading={submittingClaim}
                            />
                        </div>
                    }
                >
                    {claimingOffer && (
                        <div>
                            {/* Offer Summary */}
                            <div className="surface-100 border-round p-3 mb-3">
                                <div className="flex align-items-center gap-2 mb-2">
                                    <i className="pi pi-building text-primary" />
                                    <span className="font-bold text-900">
                                        {claimingOffer.offer.bank_name}
                                    </span>
                                    {claimingOffer.offer.product_name && (
                                        <span className="text-500 text-sm">
                                            &middot; {claimingOffer.offer.product_name}
                                        </span>
                                    )}
                                </div>
                                <Divider className="my-2" />
                                <div className="grid text-sm">
                                    <div className="col-6">
                                        <div className="text-500 mb-1">Loan Amount</div>
                                        <div className="font-bold">{
                                            claimingOffer.offer.loan_amount ? formatCurrency(claimingOffer.offer.loan_amount)
                                            : (claimingOffer.offer.min_loan_amount || claimingOffer.offer.max_loan_amount)
                                                ? `${formatCurrency(claimingOffer.offer.min_loan_amount)} - ${formatCurrency(claimingOffer.offer.max_loan_amount)}`
                                                : 'N/A'
                                        }</div>
                                    </div>
                                    <div className="col-6">
                                        <div className="text-500 mb-1">EMI</div>
                                        <div className="font-bold text-blue-600">{
                                            claimingOffer.offer.emi ? formatCurrency(claimingOffer.offer.emi) : 'N/A'
                                        }</div>
                                    </div>
                                    <div className="col-6">
                                        <div className="text-500 mb-1">Rate</div>
                                        <div className="font-bold">{claimingOffer.offer.roi || '-'}%</div>
                                    </div>
                                    <div className="col-6">
                                        <div className="text-500 mb-1">Tenure</div>
                                        <div className="font-bold">{
                                            claimingOffer.offer.tenure ? `${claimingOffer.offer.tenure} months`
                                            : (claimingOffer.offer.min_tenure || claimingOffer.offer.max_tenure)
                                                ? `${claimingOffer.offer.min_tenure} - ${claimingOffer.offer.max_tenure} months`
                                                : 'N/A'
                                        }</div>
                                    </div>
                                </div>
                            </div>

                            {/* Message Field */}
                            <div className="field">
                                <label htmlFor="claimMessage" className="font-semibold block mb-2">
                                    Message (optional)
                                </label>
                                <InputTextarea
                                    id="claimMessage"
                                    value={claimMessage}
                                    onChange={(e) => setClaimMessage(e.target.value)}
                                    rows={3}
                                    maxLength={1000}
                                    placeholder="Why are you a good fit to process this offer? e.g. bank contacts, experience..."
                                    className="w-full"
                                    autoResize
                                />
                                <small className="text-500 block mt-1">
                                    {claimMessage.length}/1000 characters
                                </small>
                            </div>
                        </div>
                    )}
                </Dialog>

                {/* Withdraw Confirmation Dialog */}
                <Dialog
                    header="Withdraw Claim"
                    visible={withdrawDialogVisible}
                    onHide={() => setWithdrawDialogVisible(false)}
                    style={{ width: '400px' }}
                    modal
                    footer={
                        <div className="flex justify-content-end gap-2">
                            <Button
                                label="Cancel"
                                icon="pi pi-times"
                                className="p-button-text"
                                onClick={() => setWithdrawDialogVisible(false)}
                                disabled={withdrawing}
                            />
                            <Button
                                label="Withdraw"
                                icon="pi pi-undo"
                                className="p-button-danger"
                                onClick={submitWithdraw}
                                loading={withdrawing}
                            />
                        </div>
                    }
                >
                    <div className="flex align-items-center gap-3">
                        <i
                            className="pi pi-exclamation-triangle text-warning"
                            style={{ fontSize: '2rem' }}
                        />
                        <span>Are you sure you want to withdraw your claim? This action cannot be undone.</span>
                    </div>
                </Dialog>

                <Tooltip target=".p-button-text" />

                {/* Approve Claim Dialog */}
                <Dialog
                    header={<div className="flex align-items-center gap-2"><i className="pi pi-check-circle text-green-500" /><span>Approve Claim</span></div>}
                    visible={approveDialogVisible}
                    style={{ width: '420px' }}
                    modal
                    footer={
                        <div className="flex justify-content-end gap-2">
                            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setApproveDialogVisible(false)} disabled={approving} />
                            <Button label="Approve" icon="pi pi-check" className="p-button-success" onClick={handleApprove} loading={approving} />
                        </div>
                    }
                    onHide={() => setApproveDialogVisible(false)}
                >
                    {approvingClaim && (
                        <div className="flex align-items-start gap-3">
                            <i className="pi pi-check-circle text-green-500" style={{ fontSize: '1.5rem', marginTop: '2px' }} />
                            <div>
                                <p className="m-0 mb-2">Approve claim by <strong>{approvingClaim.claimed_by?.name || 'this employee'}</strong>?</p>
                                <p className="m-0 text-600 text-sm">This will assign them as the processor and grant full lead access.</p>
                            </div>
                        </div>
                    )}
                </Dialog>

                {/* Reject Claim Dialog */}
                <Dialog
                    header={<div className="flex align-items-center gap-2"><i className="pi pi-times-circle text-red-500" /><span>Reject Claim</span></div>}
                    visible={rejectDialogVisible}
                    style={{ width: '420px' }}
                    modal
                    footer={
                        <div className="flex justify-content-end gap-2">
                            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setRejectDialogVisible(false)} disabled={rejecting} />
                            <Button label="Reject" icon="pi pi-ban" className="p-button-danger" onClick={handleReject} loading={rejecting} />
                        </div>
                    }
                    onHide={() => setRejectDialogVisible(false)}
                >
                    {rejectingClaim && (
                        <div>
                            <div className="flex align-items-start gap-3 mb-3">
                                <i className="pi pi-exclamation-triangle text-red-500" style={{ fontSize: '1.5rem', marginTop: '2px' }} />
                                <p className="m-0">Reject claim by <strong>{rejectingClaim.claimed_by?.name || 'this employee'}</strong>?</p>
                            </div>
                            <div className="field">
                                <label className="font-semibold block mb-2">Reason (optional)</label>
                                <InputTextarea
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                    rows={3}
                                    maxLength={500}
                                    placeholder="Explain why this claim is being rejected..."
                                    className="w-full"
                                    autoResize
                                />
                            </div>
                        </div>
                    )}
                </Dialog>

                {/* Make Processor — small confirm dialog */}
                <Dialog
                    header={<div className="flex align-items-center gap-2"><i className="pi pi-sync" style={{ color: 'var(--orange-500)' }} /><span>Change Processor</span></div>}
                    visible={!!processorConfirmTarget}
                    style={{ width: '420px' }}
                    modal
                    footer={
                        <div className="flex justify-content-end gap-2">
                            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setProcessorConfirmTarget(null)} disabled={changingProcessor} />
                            <Button label="Confirm" icon="pi pi-check" className="p-button-warning" onClick={handleChangeProcessor} loading={changingProcessor} />
                        </div>
                    }
                    onHide={() => setProcessorConfirmTarget(null)}
                >
                    {processorConfirmTarget && (
                        <div className="flex align-items-start gap-3">
                            <i className="pi pi-exclamation-triangle text-orange-500" style={{ fontSize: '1.5rem', marginTop: '2px' }} />
                            <div>
                                <p className="m-0 mb-2">
                                    Assign <strong>{processorConfirmTarget.claim.claimed_by?.name || 'this employee'}</strong> as the new processor?
                                </p>
                                <p className="m-0 text-600 text-sm">
                                    The current processor's access will be revoked and full lead access will be granted to the new processor.
                                </p>
                            </div>
                        </div>
                    )}
                </Dialog>

                {/* Regenerate Offers Dialog — secure, no lead edit access.
                    We forward the cached lead expansion data so the dialog can
                    read documents_available (which lives on leadDataCache, not
                    on the broadcast object itself). Without this the inline
                    payslip viewer can't tell whether the lead has uploaded
                    payslips and stays hidden. */}
                <RegenerateOffersDialog
                    visible={regenVisible}
                    onHide={() => { setRegenVisible(false); setRegenBroadcast(null); }}
                    broadcast={regenBroadcast}
                    leadData={regenBroadcast?.id ? leadDataCache[regenBroadcast.id] : null}
                    toast={toast}
                    onSuccess={() => {
                        // Clear cached expand data for this broadcast so fresh data loads
                        if (regenBroadcast?.id) {
                            setBroadcastDetailsCache(prev => { const next = { ...prev }; delete next[regenBroadcast.id]; return next; });
                            setLeadDataCache(prev => { const next = { ...prev }; delete next[regenBroadcast.id]; return next; });
                        }
                        loadBroadcasts(Math.floor(first / ITEMS_PER_PAGE) + 1);
                    }}
                />

                {/* Inline payslip viewer dialog. Replaces the old "open N
                    browser tabs" flow which lost 2 of 3 to popup blockers
                    (browsers allow only one window.open per user gesture).
                    Uses signed S3 URLs straight from getBroadcastPayslipUrls
                    so the iframe loads without needing auth headers.
                    Layout mirrors the OCR component's viewer for consistency. */}
                <Dialog
                    visible={payslipViewerVisible}
                    onHide={() => { setPayslipViewerVisible(false); setPayslipUrls([]); setPayslipIndex(0); }}
                    header={
                        <div className="flex align-items-center gap-2">
                            <i className="pi pi-file-pdf text-primary" />
                            <span>
                                {payslipUrls.length > 1
                                    ? `Payslip ${payslipIndex + 1} of ${payslipUrls.length}`
                                    : 'Payslip Document'}
                            </span>
                            {payslipUrls[payslipIndex]?.name && (
                                <span className="text-500 font-normal text-sm ml-1">
                                    — {payslipUrls[payslipIndex].name}
                                </span>
                            )}
                        </div>
                    }
                    style={{ width: '90vw', height: '90vh' }}
                    maximizable
                    modal
                    contentStyle={{ padding: 0, height: 'calc(90vh - 100px)' }}
                    footer={
                        <div className="flex justify-content-between align-items-center">
                            <div className="flex gap-2">
                                {payslipUrls.length > 1 && (
                                    <>
                                        <Button
                                            label="Previous"
                                            icon="pi pi-chevron-left"
                                            className="p-button-outlined p-button-sm"
                                            disabled={payslipIndex === 0}
                                            onClick={() => setPayslipIndex(i => Math.max(0, i - 1))}
                                        />
                                        <Button
                                            label="Next"
                                            icon="pi pi-chevron-right"
                                            iconPos="right"
                                            className="p-button-outlined p-button-sm"
                                            disabled={payslipIndex >= payslipUrls.length - 1}
                                            onClick={() => setPayslipIndex(i => Math.min(payslipUrls.length - 1, i + 1))}
                                        />
                                    </>
                                )}
                            </div>
                            <Button
                                label="Open in New Tab"
                                icon="pi pi-external-link"
                                className="p-button-text p-button-sm"
                                onClick={() => {
                                    const url = payslipUrls[payslipIndex]?.url;
                                    if (url) window.open(url, '_blank');
                                }}
                            />
                        </div>
                    }
                >
                    {payslipUrls[payslipIndex]?.url && (() => {
                        const ps = payslipUrls[payslipIndex];
                        const name = (ps.name || '').toLowerCase();
                        const isImage = /\.(jpg|jpeg|png|gif|bmp|webp)(\?|$)/.test(name);
                        if (isImage) {
                            return (
                                <div className="flex justify-content-center align-items-center h-full p-3 overflow-auto">
                                    <img src={ps.url} alt="Payslip" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                </div>
                            );
                        }
                        return (
                            <iframe
                                src={ps.url}
                                title={`Payslip ${payslipIndex + 1}`}
                                style={{ width: '100%', height: '100%', border: 'none' }}
                            />
                        );
                    })()}
                </Dialog>
            {/* Compact overrides — matching MyOffers/ReceivedClaims sizing exactly */}
            <style>{`
                .available-offers .p-calendar.p-inputtext-sm .p-inputtext {
                    font-size: 0.7rem !important;
                }
                .available-offers .p-calendar.p-inputtext-sm .p-datepicker-trigger {
                    width: 2rem !important;
                    padding: 0 !important;
                }
                .available-offers .p-calendar.p-inputtext-sm .p-datepicker-trigger .p-button-icon,
                .available-offers .p-calendar.p-inputtext-sm .p-datepicker-trigger .p-icon {
                    font-size: 0.75rem !important;
                    width: 0.75rem !important;
                    height: 0.75rem !important;
                }
                .available-offers .p-paginator .p-paginator-element {
                    min-width: 1.5rem !important;
                    height: 1.5rem !important;
                    font-size: 0.65rem !important;
                }
                /* Mobile: larger touch targets for paginator */
                @media (max-width: 768px) {
                    .available-offers .p-paginator .p-paginator-element {
                        min-width: 2.25rem !important;
                        height: 2.25rem !important;
                        font-size: 0.75rem !important;
                    }
                    .available-offers .p-calendar.p-inputtext-sm .p-datepicker-trigger {
                        width: 2.25rem !important;
                    }
                    .available-offers .p-calendar.p-inputtext-sm .p-inputtext {
                        font-size: 0.8rem !important;
                    }
                    .available-offers .p-dialog {
                        width: 95vw !important;
                        max-width: 95vw !important;
                    }
                }
            `}</style>
            </div>
        </div>
    );
};

export default AvailableOffers;
